<script setup>
import { computed, ref, watch } from 'vue'
import Button from 'primevue/button'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Tag from 'primevue/tag'
import { listCompactExcelProductTypeDrafts, listExcelProductTypeDraftSelectionOptions, getExcelProductTypeDraft, generateExcelProductTypeDrafts, updateExcelProductTypeDraft } from '../api/excelProductTypeDrafts.service'

const props = defineProps({ jobId: { type: String, required: true }, revisionId: { type: String, required: true }, selection: { type: Array, default: () => [] } })
const emit = defineEmits(['preview', 'selection-change'])
const page = ref(null); const details = ref(null); const form = ref(null); const state = ref('loading'); const error = ref(null)
const options = ref([]); const optionsState = ref('loading'); let sequence = 0
const pageCursors = ref([-1]); const pageIndex = ref(0)
const selectedIds = computed(() => new Set(props.selection.map(x => x.excelProductTypeDraftId)))
const optionMap = computed(() => new Map(options.value.map(x => [x.excelProductTypeDraftId, x])))
const selectedCount = computed(() => selectedIds.value.size)
const selectionReady = computed(() => optionsState.value === 'loaded' && selectedCount.value > 0 && props.selection.every(x => x.readiness === 'Ready'))
const allSelected = computed(() => options.value.length > 0 && selectedCount.value === options.value.length)
const busy = computed(() => ['loading', 'generating', 'saving', 'details'].includes(state.value))
const fields = [
  { key: 'name', label: 'Название' }, { key: 'modelCode', label: 'Код модели' },
  { key: 'description', label: 'Описание' }, { key: 'collectionName', label: 'Коллекция' },
  { key: 'brand', label: 'Бренд' }, { key: 'category', label: 'Категория' }
]
function emitSelection(ids) { emit('selection-change', ids.map(id => optionMap.value.get(id)).filter(Boolean).map(x => ({ ...x }))) }
function toggle(id) {
  const ids = new Set(selectedIds.value)
  if (ids.has(id)) ids.delete(id); else ids.add(id)
  emitSelection([...ids])
}
async function loadOptions(request) {
  optionsState.value = 'loading'
  const result = await listExcelProductTypeDraftSelectionOptions(props.jobId, props.revisionId)
  if (request !== sequence) return
  options.value = result.items || []; optionsState.value = 'loaded'
  if (props.selection.length) emitSelection([...selectedIds.value])
}
async function load(after = -1, generate = false) {
  const request = ++sequence
  state.value = generate ? 'generating' : 'loading'; error.value = null; details.value = null; form.value = null
  try {
    if (generate) await generateExcelProductTypeDrafts(props.jobId, props.revisionId)
    const result = await listCompactExcelProductTypeDrafts(props.jobId, props.revisionId, after)
    if (request !== sequence) return
    page.value = result // bounded page; full DTO is never part of the list
    if (generate || after === -1) { pageCursors.value = [-1]; pageIndex.value = 0 }
    if (after === -1 || generate) await loadOptions(request)
    if (request === sequence) state.value = 'loaded'
  } catch (e) { if (request === sequence) { error.value = e.message; state.value = 'error' } }
}
function nextPage() {
  if (page.value?.nextAfterItemOrder == null) return
  pageCursors.value = [...pageCursors.value.slice(0, pageIndex.value + 1), page.value.nextAfterItemOrder]
  pageIndex.value++
  load(pageCursors.value[pageIndex.value])
}
function previousPage() {
  if (pageIndex.value === 0) return
  pageIndex.value--
  load(pageCursors.value[pageIndex.value])
}
async function openDetails(id) {
  if (details.value?.id === id) { closeDetails(); return }
  const request = ++sequence
  details.value = null; form.value = null; state.value = 'details'; error.value = null
  try {
    const draft = await getExcelProductTypeDraft(props.jobId, props.revisionId, id)
    if (request !== sequence) return
    details.value = draft; form.value = { ...draft.values }; state.value = 'loaded'
  } catch (e) { if (request === sequence) { error.value = e.message; state.value = 'error' } }
}
function closeDetails() { sequence++; details.value = null; form.value = null; state.value = 'loaded' }
async function save() {
  if (!details.value) return
  const request = ++sequence
  state.value = 'saving'; error.value = null
  try {
    const draft = await updateExcelProductTypeDraft(props.jobId, props.revisionId, details.value.id,
      { expectedVersion: details.value.concurrencyVersion, values: form.value })
    if (request !== sequence) return
    details.value = draft; form.value = { ...draft.values }
    page.value.items = page.value.items.map(row => row.excelProductTypeDraftId === draft.id
      ? { ...row, name: draft.values.name, modelCode: draft.values.modelCode, concurrencyVersion: draft.concurrencyVersion, readiness: draft.readiness, reviewStatus: draft.reviewStatus } : row)
    const option = optionMap.value.get(draft.id)
    if (option) {
      option.concurrencyVersion = draft.concurrencyVersion; option.readiness = draft.readiness
      if (selectedIds.value.has(draft.id)) emitSelection([...selectedIds.value])
    }
    state.value = 'loaded'
  } catch (e) { if (request === sequence) { error.value = e.message; state.value = 'error' } }
}
watch(() => [props.jobId, props.revisionId], () => { sequence++; page.value = null; options.value = []; optionsState.value = 'loading'; pageCursors.value = [-1]; pageIndex.value = 0; load() }, { immediate: true })
</script>

<template>
  <section aria-label="Черновики типов товаров">
    <h2>Типы товаров — черновики</h2>
    <div class="actions">
      <Button label="Сформировать черновики" :disabled="busy" @click="load(-1, true)" />
      <Button label="Обновить" outlined :disabled="busy" @click="load()" />
      <Button label="Выбрать все" outlined :disabled="busy || optionsState !== 'loaded' || allSelected" @click="emitSelection(options.map(x => x.excelProductTypeDraftId))" />
      <Button label="Снять все" outlined :disabled="busy || !selectedCount" @click="emitSelection([])" />
      <Button label="К товарам" :disabled="busy || !selectionReady" @click="emit('preview', props.selection)" />
    </div>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-if="page">Всего: {{ page.total }}. Выбрано: {{ selectedCount }}. Показана страница: {{ page.items.length }}.</p>
    <template v-for="row in page?.items || []" :key="row.excelProductTypeDraftId">
      <div class="compact-row">
        <input type="checkbox" :checked="selectedIds.has(row.excelProductTypeDraftId)" @change="toggle(row.excelProductTypeDraftId)" />
        <strong>{{ row.name || 'Без названия' }}</strong><span>{{ row.modelCode || '—' }}</span>
        <Tag :value="row.readiness" :severity="row.readiness === 'Ready' ? 'success' : 'warn'" />
        <Tag :value="row.reviewStatus" severity="secondary" />
        <Button :label="details?.id === row.excelProductTypeDraftId ? 'Скрыть' : 'Подробнее / изменить'" text :disabled="busy" @click="openDetails(row.excelProductTypeDraftId)" />
      </div>
      <Card v-if="details?.id === row.excelProductTypeDraftId" class="details-card">
        <template #title>{{ details.values.name || 'Без названия' }}</template>
        <template #content>
          <form @submit.prevent="save">
            <label v-for="field in fields" :key="field.key">{{ field.label }}
              <Textarea v-if="field.key === 'description'" v-model="form[field.key]" rows="3" />
              <InputText v-else v-model="form[field.key]" />
            </label>
            <div class="actions"><Button type="submit" label="Сохранить" :disabled="busy" /><Button type="button" label="Закрыть" outlined :disabled="state === 'saving'" @click="closeDetails" /></div>
          </form>
          <p>Бренд: {{ details.matches.brand.state }}; категория: {{ details.matches.category.state }}.</p>
          <ul><li v-for="(issue, index) in details.issues" :key="index">{{ issue.code }}: {{ issue.message }}</li></ul>
          <details><summary>Исходные строки и provenance</summary><pre>{{ JSON.stringify(details.source, null, 2) }}</pre></details>
        </template>
      </Card>
    </template>
    <div v-if="page" class="actions">
      <Button label="Предыдущая страница" outlined :disabled="busy || pageIndex === 0" @click="previousPage" />
      <Button label="Следующая страница" outlined :disabled="busy || page.nextAfterItemOrder == null" @click="nextPage" />
    </div>
  </section>
</template>

<style scoped>
.actions { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
.compact-row { display: flex; gap: 12px; align-items: center; padding: 8px; border-bottom: 1px solid #ddd; }
.compact-row strong { min-width: 180px; }.details-card form { display: grid; gap: 10px; }
.details-card label { display: grid; gap: 4px; }.details-card pre { max-height: 360px; overflow: auto; }
</style>
